import {useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {evaluateLDFlag} from 'launchdarkly/LaunchDarklyUtil';
import makeUseGlobalClientState, {
  UseGlobalClientStateOutput,
} from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {assertNever} from 'util/Types';

import type {ChatDraftState} from '../ChatDraftState';
import {setDraftState} from '../ChatDraftState';
import type {SidepanelChat} from './ChatRouter';

const AI_PANEL_STATE_KEY = 'ai-panel-state-v2';

/** Whether the chat redesign is enabled. */
function isChatRedesignEnabled(): boolean {
  return evaluateLDFlag('LLM_ENABLE_CHAT_REDESIGN') === true;
}

/**
 * Sentinel value for `showHistory` / `showHistoryPreference` meaning "use the flag-derived default".
 *
 * The stored redux default is this sentinel, and the public API
 * ({@link useAISidepanelGlobalClientState} / {@link getAISidepanelState}) resolves it to a boolean
 * at read time via the LD flag. This keeps the redesign's "drawer defaults open" behavior in the
 * state layer (rather than a mount effect) while avoiding evaluating the flag at module-import time,
 * when the LaunchDarkly client may not yet be initialized.
 */
const USE_LD_DEFAULT = 'USE_LD_DEFAULT';
type ShowHistoryState = boolean | typeof USE_LD_DEFAULT;

/** Resolve a stored show-history value; the sentinel maps to the redesign default (open when on). */
function resolveShowHistory(value: ShowHistoryState): boolean {
  return value === USE_LD_DEFAULT ? isChatRedesignEnabled() : value;
}

/**
 * Tabs within the AI settings view.
 */
export enum AISettingsTab {
  GENERAL = 'GENERAL',
  SKILLS = 'SKILLS',
  AI_CONNECTORS = 'AI_CONNECTORS',
  ROUTINES = 'ROUTINES',
}

/**
 * Indicates where to route within the AI sidepanel.
 *
 * Enum values are directly encoded in the URL shared in marketing emails
 * (see AIRoutes.openDraftChatFromQueryString), and therefore should not be changed.
 * The query string variable "modal" is a misnomer.
 */
export enum InitialUI {
  SIGNUP = 'signup',
  SETTINGS = 'settings',
}

export enum InitialModal {
  SIGNUP_MODAL = 'SIGNUP_MODAL',
}

/**
 * Which feature is active in the chat settings modal. General is the default landing tab.
 *
 * These are internal identifiers, not URL segments; the stable URL segment for each is produced by
 * the exhaustive mapping in UrlUtils (chatSettingsTabToUrlSegment).
 */
export enum ChatSettingsTab {
  GENERAL = 'GENERAL',
  SKILLS = 'SKILLS',
  AGENTS = 'AGENTS',
  CONNECTORS = 'CONNECTORS',
  USE_CASES = 'USE_CASES',
}

/**
 * In-modal grouping filter for the Skills tab. Defaults to ALL.
 */
export enum SkillsGroup {
  ALL = 'ALL',
  MINE = 'MINE',
  SHARED = 'SHARED',
}

/**
 * In-modal grouping filter for the Background agents tab. Defaults to ALL. `EXAMPLE_AGENTS` is a
 * read-only group listing pre-configured example agents that seed the create form when clicked.
 */
export enum AgentsGroup {
  ALL = 'ALL',
  MINE = 'MINE',
  SHARED = 'SHARED',
  EXAMPLE_AGENTS = 'EXAMPLE_AGENTS',
}

/**
 * In-modal grouping filter for the Connectors tab. Defaults to AVAILABLE.
 */
export enum ConnectorsGroup {
  CONNECTED = 'CONNECTED',
  AVAILABLE = 'AVAILABLE',
  AWAITING_SETUP = 'AWAITING_SETUP',
}

/**
 * URL-derived state for the chat settings modal, as a discriminated union on `tab`. `null` (on
 * AIPanelState.chatSettingsModal) means the modal is closed.
 *
 * The union encodes which fields each tab supports: General is a plain settings form and Use cases
 * is a single list whose category filter is owned by the panel itself (no detail, no group state);
 * Skills, Agents, and Connectors are list/detail views with a group filter. The
 * route handlers in AIRoutes write the tab and selected item from the URL and seed each list tab
 * with its default group; the group is not URL-encoded (it can go stale, e.g. an item moved to
 * another group), so in-modal filtering is owned locally. Deeper in-modal navigation (sub-details)
 * is likewise handled locally and is not encoded here.
 */
export type ChatSettingsModalState =
  | {tab: ChatSettingsTab.GENERAL}
  | {tab: ChatSettingsTab.SKILLS; selectedItemId: string | null; group: SkillsGroup}
  | {tab: ChatSettingsTab.AGENTS; selectedItemId: string | null; group: AgentsGroup}
  | {tab: ChatSettingsTab.CONNECTORS; selectedItemId: string | null; group: ConnectorsGroup}
  | {tab: ChatSettingsTab.USE_CASES};

export interface AIPanelState {
  /**
   * A client-local ID used as the key for the ChatRouter component. This allows us to ensure that
   * state does not leak between chat sessions when rendering ChatRouter. This is simply not the chat
   * ID since that does not exist when a new chat is started.
   */
  clientChatId: string;

  /**
   * The chat session that is currently active.
   */
  activeChat: SidepanelChat;

  // Whether the history is shown.
  showHistory: boolean;

  // The user's preference for whether history is shown.
  showHistoryPreference: boolean;

  initialModal: InitialModal | undefined;

  activeSettingsTab: AISettingsTab | null;

  // Whether the user has clicked "Start" on the onboarding banner, bypassing it to show the full
  // onboarding screen directly. Persisted in global state so navigation away and back doesn't reset it.
  showOnboardingScreen: boolean;

  // URL-derived state for the chat settings modal, or null when it is closed.
  // Written only by the AIRoutes route handlers (the URL is the source of truth).
  chatSettingsModal: ChatSettingsModalState | null;

  // Whether the Agent Factories list view is showing in the sidepanel's main content slot (the same
  // slot the chat occupies). Written only by the AIRoutes route handler so it follows the URL
  // (/chats/agent-factories). Selecting a factory opens it as its own workspace tab (see
  // EditorRoutes.agentFactoryEditor) rather than a variant of this view, so a factory and a chat can
  // be open side by side.
  showAgentFactoriesView: boolean;
}

/**
 * Internal (stored) shape. Identical to {@link AIPanelState} except the show-history fields may hold
 * the {@link USE_LD_DEFAULT} sentinel, which the read boundary resolves to a boolean.
 */
type StoredAIPanelState = Omit<AIPanelState, 'showHistory' | 'showHistoryPreference'> & {
  showHistory: ShowHistoryState;
  showHistoryPreference: ShowHistoryState;
};

export const panelStateHook = makeUseGlobalClientState<StoredAIPanelState>({
  activeChat: {
    id: null,
  },
  // The panel opens in the semi-expanded (collapsed) view, where the sidebar defaults closed; it
  // defaults open only in the expanded view. `useHistoryAutoHide` opens it on expand (from the
  // preference) and closes it on collapse. The preference default is flag-derived (open in the
  // redesign) and resolved lazily so the flag is read at usage time, not module-import time.
  showHistory: false,
  showHistoryPreference: USE_LD_DEFAULT,
  clientChatId: uuidv4(),
  initialModal: undefined,
  activeSettingsTab: null,
  showOnboardingScreen: false,
  chatSettingsModal: null,
  showAgentFactoriesView: false,
});

/** Resolve the sentinel show-history fields to booleans for consumers. */
function resolvePanelState(stored: StoredAIPanelState): AIPanelState {
  return {
    ...stored,
    showHistory: resolveShowHistory(stored.showHistory),
    showHistoryPreference: resolveShowHistory(stored.showHistoryPreference),
  };
}

/** Raw stored state, preserving the sentinel. For internal state writes that must not clobber it. */
function getStoredPanelState(): StoredAIPanelState {
  return panelStateHook.getState(AI_PANEL_STATE_KEY);
}

export function useAISidepanelGlobalClientState(): UseGlobalClientStateOutput<AIPanelState> {
  const [stored, setStored] = panelStateHook.useGlobalClientState(AI_PANEL_STATE_KEY);
  const setState = useCallback(
    (next: AIPanelState) => {
      setStored(next);
    },
    [setStored]
  );
  return [resolvePanelState(stored), setState];
}

export function getAISidepanelState(): AIPanelState {
  return resolvePanelState(getStoredPanelState());
}

export function openExistingChat(chatSessionId: string): void {
  const stored = getStoredPanelState();
  const redesign = isChatRedesignEnabled();
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: null},
    // Redesign: the sidebar toggle is only changed by the user, so preserve it here. Legacy: opening
    // a chat hides the history drawer.
    showHistory: redesign ? stored.showHistory : false,
    showHistoryPreference: redesign ? stored.showHistoryPreference : false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
    chatSettingsModal: null,
    showAgentFactoriesView: false,
  });
}

/** Like openExistingChat, but preserves a known chat type so ChatRouter can render immediately. */
export function remountExistingChatWithType(chatSessionId: string, chatType: ChatType): void {
  const stored = getStoredPanelState();
  const redesign = isChatRedesignEnabled();
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: chatType},
    showHistory: redesign ? stored.showHistory : false,
    showHistoryPreference: redesign ? stored.showHistoryPreference : false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
    chatSettingsModal: null,
    showAgentFactoriesView: false,
  });
}

export function openDraftChat(draftState: ChatDraftState, options: {initialUI?: InitialUI} = {}): void {
  setDraftState(null, draftState);

  let initialUIState: Partial<AIPanelState> = {};
  if (options.initialUI != null) {
    switch (options.initialUI) {
      case InitialUI.SIGNUP:
        initialUIState = {initialModal: InitialModal.SIGNUP_MODAL};
        break;
      case InitialUI.SETTINGS:
        initialUIState = {activeSettingsTab: AISettingsTab.GENERAL};
        break;
      default:
        assertNever(options.initialUI);
    }
  }

  const stored = getStoredPanelState();
  const redesign = isChatRedesignEnabled();
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: null},
    showHistory: redesign ? stored.showHistory : false,
    showHistoryPreference: redesign ? stored.showHistoryPreference : false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
    chatSettingsModal: null,
    showAgentFactoriesView: false,
    ...initialUIState,
  });
}

export function openSettings(tab?: AISettingsTab): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    ...getStoredPanelState(),
    activeSettingsTab: tab ?? AISettingsTab.GENERAL,
  });
}

export function getActiveChatId(): string | null {
  return getStoredPanelState().activeChat.id;
}

/**
 * Open (or update) the chat settings modal. Called by the AIRoutes route handlers so that the
 * modal's open state, active tab, selected item, and group all follow the URL. The handler owns
 * validating the URL and building the tab-appropriate state (see AIRoutes); this just writes it.
 */
export function openChatSettingsModal(modal: ChatSettingsModalState): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    ...getStoredPanelState(),
    chatSettingsModal: modal,
  });
}

/** Close the chat settings modal. */
export function closeChatSettingsModal(): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    ...getStoredPanelState(),
    chatSettingsModal: null,
  });
}

/**
 * Open the Agent Factories list view in the sidepanel's main content slot. Called by the AIRoutes
 * route handler so the view follows the URL, mirroring {@link openChatSettingsModal}.
 */
export function openAgentFactoriesView(): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    ...getStoredPanelState(),
    // Onboarding is checked before `showAgentFactoriesView` when choosing what to render in the main
    // content slot (see AISidepanelV2), so it must be cleared here or it would keep showing instead.
    showOnboardingScreen: false,
    showAgentFactoriesView: true,
  });
}
